import React, { useState } from 'react';
import { oracleDeck, uiText } from '../../data/oracle/oracleDeckData';
import { useGlow } from '../../hooks/useGlow';
import { 
  SparklesIcon, 
  ArrowPathRoundedSquareIcon, 
  TrashIcon, 
  PlusIcon 
} from '@heroicons/react/24/outline';
import { getOracleReading } from '../../services/geminiService'; 
import AIReadingDisplay from './AIReadingDisplay'; 

const ManualThreeCardOracle = ({ lang = 'es' }) => {
  const [selectedCards, setSelectedCards] = useState([null, null, null]);
  const [topic, setTopic] = useState('');
  const [aiReading, setAiReading] = useState('');
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [showSelectorFor, setShowSelectorFor] = useState(null);

  const ui = uiText[lang] || uiText['es'];
  const localUi = {
    title: lang === 'en' ? 'Manual Oracle Selection' : 'Selección Manual del Oráculo',
    subtitle: lang === 'en' ? 'Choose your cards and define their path.' : 'Elige tus cartas y define su camino.',
    pos1: lang === 'en' ? '1. The Situation' : '1. La Situación',
    pos2: lang === 'en' ? '2. The Obstacle' : '2. El Obstáculo',
    pos3: lang === 'en' ? '3. The Advice' : '3. El Consejo',
    placeholder: lang === 'en' ? 'Describe the topic...' : 'Describe el tema o pregunta...',
    btnAi: lang === 'en' ? 'Interpret these Cards' : 'Interpretar estas Cartas',
    selectCard: lang === 'en' ? 'Select Card' : 'Elegir Carta',
    changeCard: lang === 'en' ? 'Change' : 'Cambiar',
  };

  const titleStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #9b977aff, #9e823cff, #776f5cff)' });

  // --- LÓGICA ACTUALIZADA ---

  const handleSelectCard = (cardData, slotIndex) => {
    const newSelected = [...selectedCards];
    newSelected[slotIndex] = {
      ...cardData,
      isReversed: false,
      data: cardData[lang] // Guardamos toda la traducción (name, name2, name3, category)
    };
    setSelectedCards(newSelected);
    setShowSelectorFor(null);
  };

  const toggleReverse = (index) => {
    const newSelected = [...selectedCards];
    if (newSelected[index]) {
      newSelected[index].isReversed = !newSelected[index].isReversed;
      setSelectedCards(newSelected);
    }
  };

  const removeCard = (index) => {
    const newSelected = [...selectedCards];
    newSelected[index] = null;
    setSelectedCards(newSelected);
    setAiReading('');
  };

  const handleGetAIReading = async () => {
    if (selectedCards.some(c => c === null) || !topic.trim()) return;
    
    setIsLoadingAi(true);
    const cardsForAi = selectedCards.map((c, index) => ({
      // Pasamos un nombre compuesto que incluye los 3 significados y la categoría para la IA
      name: `${c.data.name} (${c.data.name2}, ${c.data.name3}) - Categoría: ${c.data.category}`,
      positionName: index === 0 ? localUi.pos1 : index === 1 ? localUi.pos2 : localUi.pos3,
      isReversed: c.isReversed,
      breaf: c.data.breaf || c.data.description,
      keywords: c.data.keywords
    }));

    const reading = await getOracleReading(topic, localUi.title, cardsForAi, lang);
    reading.success && setAiReading(reading.text);
    setIsLoadingAi(false);
  };

  const allCardsSelected = selectedCards.every(c => c !== null);

  return (
    <div className="w-full max-w-6xl mx-auto p-4 md:p-8 min-h-screen flex flex-col items-center">
      
      <div className="text-center mb-10">
        <h1 style={titleStyle} className="text-3xl md:text-5xl font-bold mb-2 uppercase tracking-tighter">
          {localUi.title}
        </h1>
        <p className="text-gray-400">{localUi.subtitle}</p>
      </div>

      <div className="w-full max-w-xl mb-12">
        <input 
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder={localUi.placeholder}
          className="w-full bg-gray-900/50 border-b-2 border-purple-500/30 text-white p-3 text-center text-xl focus:outline-none focus:border-purple-500 transition-all"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 w-full place-items-center">
        {selectedCards.map((card, idx) => (
          <div key={idx} className="flex flex-col items-center gap-4 w-full">
            <span className="text-purple-300 text-xs font-bold tracking-widest uppercase opacity-60">
              {idx === 0 ? localUi.pos1 : idx === 1 ? localUi.pos2 : idx === 2 ? localUi.pos3 : ''}
            </span>

            <div className="relative group">
              {card ? (
                <div className="relative flex flex-col items-center">
                  <div className={`w-44 h-64 sm:w-48 sm:h-72 rounded-xl overflow-hidden border-2 border-sky-400/50 shadow-2xl transition-transform duration-500 ${card.isReversed ? 'rotate-180' : ''}`}>
                    <img src={card.image} alt={card.data.name} className="w-full h-full object-cover" />
                  </div>
                  
                  <div className="absolute -right-4 -top-4 flex flex-col gap-2 z-10">
                    <button onClick={() => toggleReverse(idx)} className="p-2 bg-gray-800 text-white rounded-full hover:bg-purple-600 transition-colors shadow-lg border border-white/10">
                      <ArrowPathRoundedSquareIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => removeCard(idx)} className="p-2 bg-gray-800 text-red-400 rounded-full hover:bg-red-900 transition-colors shadow-lg border border-white/10">
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* INFO DEBAJO DE LA CARTA SELECCIONADA */}
                  <div className="mt-4 text-center flex flex-col items-center gap-1 animate-fade-in">
                    <span className="text-sky-400 text-[10px] font-bold tracking-widest uppercase bg-sky-950/30 px-2 py-0.5 rounded border border-sky-500/20">
                        {card.data.category}
                    </span>
                    <p className="text-white font-bold text-lg leading-tight uppercase">{card.data.name}</p>
                    <p className="text-gray-400 text-[10px] italic font-medium max-w-[160px]">
                        {card.data.name2} <span className="text-gray-600">•</span> {card.data.name3}
                    </p>
                    {card.isReversed && (
                        <span className="text-red-400 text-[9px] font-black uppercase tracking-tighter border border-red-400/30 px-2 py-0.5 rounded-full mt-1 bg-red-900/20">
                            Invertida
                        </span>
                    )}
                  </div>
                </div>
              ) : (
                <button 
                  onClick={() => setShowSelectorFor(idx)}
                  className="w-44 h-64 sm:w-48 sm:h-72 rounded-xl border-2 border-dashed border-gray-700 flex flex-col items-center justify-center gap-3 hover:border-purple-500 hover:bg-purple-500/5 transition-all group"
                >
                  <div className="p-4 rounded-full bg-gray-800 group-hover:bg-purple-600 transition-colors">
                    <PlusIcon className="w-8 h-8 text-gray-400 group-hover:text-white" />
                  </div>
                  <span className="text-gray-500 text-sm font-medium">{localUi.selectCard}</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="w-full max-w-3xl flex flex-col items-center mb-20">
        {!aiReading && !isLoadingAi ? (
          <button 
            onClick={handleGetAIReading}
            disabled={!allCardsSelected || !topic.trim()}
            className={`flex items-center gap-2 py-4 px-10 rounded-full font-bold text-lg transition-all
              ${allCardsSelected && topic.trim() 
                ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-900/50 scale-110' 
                : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`}
          >
            <SparklesIcon className="w-6 h-6" />
            {localUi.btnAi}
          </button>
        ) : (
          <AIReadingDisplay 
            interpretation={aiReading} 
            isLoading={isLoadingAi} 
            topic={topic}
            cards={selectedCards.map((c, i) => ({
                data: c.data, // Pasamos el objeto de datos completo
                image: c.image,
                isReversed: c.isReversed,
                positionName: i === 0 ? localUi.pos1 : i === 1 ? localUi.pos2 : localUi.pos3
            }))}
            spreadName={localUi.title}
          />
        )}
      </div>

      {/* MODAL / SELECTOR DE CARTAS */}
      {showSelectorFor !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/90 backdrop-blur-md animate-fade-in">
          <div className="bg-gray-950 border border-purple-500/30 w-full max-w-5xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col">
            
            <div className="p-4 border-b border-gray-800 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                {localUi.selectCard}
              </h3>
              <button onClick={() => setShowSelectorFor(null)} className="text-gray-400 hover:text-white text-3xl px-2">&times;</button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-6 p-4 md:p-6 overflow-y-auto">
              {oracleDeck.map((card, index) => {
                const isSelected = selectedCards.some(sc => sc?.id === card.id);
                const cardTranslation = card[lang];

                return (
                  <div 
                    key={card.id}
                    onClick={() => !isSelected && handleSelectCard(card, showSelectorFor)}
                    className={`flex flex-col gap-2 transition-all duration-300 
                      ${isSelected ? 'opacity-30 grayscale pointer-events-none' : 'hover:scale-105 active:scale-95 cursor-pointer'}`}
                  >
                    <div className="flex flex-col leading-tight">
                      <span className="text-[9px] text-sky-400 font-bold uppercase tracking-tighter">
                        {cardTranslation.category}
                      </span>
                      <span className="text-[11px] md:text-xs text-white font-bold uppercase truncate">
                        {cardTranslation.name}
                      </span>
                    </div>

                    <div className={`relative aspect-[2/3] rounded-lg overflow-hidden border-2 
                      ${isSelected ? 'border-transparent' : 'border-gray-800 hover:border-purple-500 shadow-lg'}`}>
                      <img src={card.image} alt={cardTranslation.name} className="w-full h-full object-cover" />
                      {isSelected && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="text-[10px] bg-white text-black px-2 py-0.5 rounded font-black">ELEGIDA</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualThreeCardOracle;